"use client";

import { useEffect } from "react";
import * as Sentry from "@sentry/nextjs";

// Root error boundary — catches errors in the root layout itself (where the
// normal app/error.tsx can't run). Must render its own <html>/<body>, so it's
// self-contained with inline styles (the app's CSS may not be available here).
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
    console.error("Global error:", error);
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#f7f2ea",
          color: "#393331",
          fontFamily: "Helvetica, Arial, sans-serif",
          textAlign: "center",
          padding: "0 24px",
        }}
      >
        <div style={{ maxWidth: 380 }}>
          <p style={{ letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 12, color: "#7a6d61", margin: "0 0 12px" }}>
            Aluume
          </p>
          <h1 style={{ fontWeight: 400, fontSize: 24, margin: "0 0 12px" }}>Something went wrong</h1>
          <p style={{ fontSize: 14, lineHeight: 1.6, color: "rgba(57,51,49,0.75)", margin: "0 0 24px" }}>
            The app hit an unexpected error. Please try again. If it keeps happening, reload the page or let us know.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
            <button
              onClick={() => reset()}
              style={{ background: "#393331", color: "#f7f2ea", border: "none", borderRadius: 999, padding: "12px 28px", fontSize: 13, cursor: "pointer" }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{ background: "none", border: "none", color: "rgba(57,51,49,0.7)", fontSize: 12, textDecoration: "underline", cursor: "pointer" }}
            >
              Reload the page
            </button>
          </div>
          {process.env.NODE_ENV !== "production" && (
            <p style={{ fontSize: 12, color: "#9b4a3f", marginTop: 24, wordBreak: "break-word" }}>
              {error.message}
              {error.digest ? ` · ${error.digest}` : ""}
            </p>
          )}
        </div>
      </body>
    </html>
  );
}
